import React from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { 
  FaCode, 
  FaPen, 
  FaDatabase, 
  FaVideo,
  FaReact,
  FaJs,
  FaCss3,
  FaBook,
  FaChartLine,
  FaPython,
  FaLock,
  FaSearch,
  FaNetworkWired,
  FaShieldAlt
} from 'react-icons/fa';

const Services = ({ currentTheme }) => {
  const services = [
    {
      title: "Frontend Development",
      icon: <FaCode />,
      color: "primary",
      description: "Creating responsive and interactive web applications with modern technologies.",
      skills: [
        { name: "React Development", icon: <FaReact /> },
        { name: "JavaScript", icon: <FaJs /> },
        { name: "CSS & Animations", icon: <FaCss3 /> },
        { name: "Python", icon: <FaPython/> }
      ]
    },
    {
      title: "Backend Development",
      icon: <FaDatabase />,
      color: "danger",
      description: "Designing and implementing robust server-side logic and database integration.",
      skills: [
        { name: "Node.js", icon: <FaCode /> },
        { name: "Express.js", icon: <FaCode /> },
        { name: "Database Management", icon: <FaDatabase /> },
        { name: "API Development", icon: <FaCode /> }
      ]
    },
    {
      title: "Technical Writing",
      icon: <FaPen />,
      color: "success",
      description: "Crafting comprehensive technical documentation and research papers.",
      skills: [
        { name: "Documentation", icon: <FaBook /> },
        { name: "Research Papers", icon: <FaSearch /> },
        { name: "Technical Guides", icon: <FaPen /> },
        { name: "API Documentation", icon: <FaCode /> }
      ]
    },
    {
      title: "Research Writing",
      icon: <FaBook />,
      color: "info",
      description: "Conducting in-depth research and creating insightful content.",
      skills: [
        { name: "Research Analysis", icon: <FaSearch /> },
        { name: "Content Creation", icon: <FaPen /> },
        { name: "Academic Writing", icon: <FaBook /> },
        { name: "Publishing", icon: <FaShieldAlt /> }
      ]
    },
    {
      title: "Data Science",
      icon: <FaChartLine />,
      color: "warning",
      description: "Analyzing data and creating insights through advanced analytics.",
      skills: [
        { name: "Data Analysis", icon: <FaChartLine /> },
        { name: "Python", icon: <FaPython /> },
        { name: "Machine Learning", icon: <FaDatabase /> },
        { name: "Visualization", icon: <FaChartLine /> }
      ]
    },
    {
      title: "CCTV Solutions",
      icon: <FaVideo />,
      color: "secondary",
      description: "Installing and maintaining professional surveillance systems.",
      skills: [
        { name: "Installation", icon: <FaVideo /> },
        { name: "Network Setup", icon: <FaNetworkWired /> },
        { name: "Security Config", icon: <FaLock /> },
        { name: "Monitoring", icon: <FaShieldAlt /> }
      ]
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5
      }
    }
  };

  return (
    <Container fluid className="py-5 position-relative overflow-hidden" id='service' style={{ backgroundColor: currentTheme.background }}>
      <div className="position-absolute w-100 h-100 top-0 start-0" style={{ zIndex: -1 }}>
        <motion.div
          animate={{
            background: [
              `linear-gradient(45deg, ${currentTheme.primary} 0%, ${currentTheme.secondary} 100%)`,
              `linear-gradient(45deg, ${currentTheme.secondary} 0%, ${currentTheme.primary} 100%)`
            ],
          }}
          transition={{ duration: 3, repeat: Infinity, repeatType: 'reverse' }}
          className="w-100 h-100"
        />
      </div>

      <Container className="position-relative">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={containerVariants}
        >
          <Row className="justify-content-center mb-5">
            <Col lg={8} className="text-center">
              <motion.h2 
                className="display-4 fw-bold mb-4"
                variants={itemVariants}
                style={{ color: currentTheme.primary }}
              >
                SERVICES
              </motion.h2>
              <motion.div 
                className="w-25 mx-auto border-bottom border-3 mb-4"
                variants={itemVariants}
                style={{ borderColor: currentTheme.primary }}
              />
            </Col>
          </Row>

          <Row className="row-cols-1 row-cols-md-2 g-4">
            {services.map((service, index) => (
              <Col key={index}>
                <motion.div
                  variants={itemVariants}
                  className="card h-100 border-0 shadow-sm"
                  style={{ 
                    backgroundColor: currentTheme.background === '#121212' ? '#222222' : 'rgba(255, 255, 255, 0.9)',
                    borderColor: currentTheme.primary,
                    borderWidth: '1px',
                    borderStyle: 'solid'
                  }}
                >
                  <div className="card-body p-4">
                    <div className="d-flex align-items-center mb-4">
                      <div className={`icon-box me-3 rounded-circle p-3`} style={{ backgroundColor: `rgba(${currentTheme.primary}, 0.1)` }}>
                        {React.cloneElement(service.icon, { style: { fontSize: '2rem', color: currentTheme.primary } })}
                      </div>
                      <h3 className="h4 mb-0" style={{ color: currentTheme.text }}>{service.title}</h3>
                    </div>
                    <p className="card-text mb-4" style={{ color: currentTheme.text }}>{service.description}</p>
                    <h4 className="h5 mb-3" style={{ color: currentTheme.primary }}>Key Skills:</h4>
                    <Row>
                      {service.skills.map((skill, skillIndex) => (
                        <Col key={skillIndex} xs={6} className="mb-3">
                          <div className="d-flex align-items-center">
                            <span className="me-2" style={{ color: currentTheme.primary }}>{skill.icon}</span>
                            <span style={{ color: currentTheme.text }}>{skill.name}</span>
                          </div>
                        </Col>
                      ))}
                    </Row>
                  </div>
                </motion.div>
              </Col>
            ))}
          </Row>
        </motion.div>
      </Container>
    </Container>
  );
};

export default Services;